import { useEffect, useState } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import {
    RenderComponentResolver,
    UniformComposition,
    UniformPlayground
} from "@uniformdev/canvas-react";

import { expectContent } from "@/content/utils/expectContent";

import { enhanceComposition } from "../clients/uniform-client/enhancers";
import { parseRoute } from "../wac-routes";

import UNIFORM_DEFINITIONS from "./uniform-definitions";
import { UniformMissingComponent } from "./utils/uniform-missing-component";

declare global {
    interface Window {
        _uniformPreloadedComposition?: RootComponentInstance;
    }
}

export type UniformWrapperProps = {
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    isStaticFallback?: boolean;
};

const resolveRenderer: RenderComponentResolver = ({ type }) => {
    const MissingComponent = () => (
        <UniformMissingComponent componentName={type} />
    );
    return UNIFORM_DEFINITIONS[type] ?? MissingComponent;
};

export const UniformWrapper = ({
    composition,
    isUniformPlayground,
    isStaticFallback
}: UniformWrapperProps) => {
    const [data, setData] = useState<unknown>();
    const [isFetched, setIsFetched] = useState(false);

    useEffect(() => {
        const fetchComposition = async () => {
            setData(composition);
            if (
                typeof window !== "undefined" &&
                window._uniformPreloadedComposition &&
                !isStaticFallback
            ) {
                // @ts-ignore
                // eslint-disable-next-line react-hooks/exhaustive-deps
                isUniformPlayground = window.isUniformPlayground;
                setData(window._uniformPreloadedComposition);
                setIsFetched(true);
            } else if (
                (typeof window !== "undefined" || isStaticFallback) &&
                !isFetched
            ) {
                // Static Fallback where we did not preload the composition
                const { path, locale } = parseRoute(window.location);
                const slug = path.startsWith("/") ? path.substring(1) : path;

                const cdn = process.env["UNIFORM_CDN"];
                const cdn_path = `${cdn}release/${locale}/${slug}/composition.json`;

                try {
                    const req = await fetch(cdn_path);
                    const file = await req.json();
                    const enhancedComp = await enhanceComposition(
                        file.compositionApiResponse.composition
                    );
                    setData(enhancedComp);
                } catch (error) {
                    console.error(
                        "Error fetching composition from CDN:",
                        error
                    );
                }

                setIsFetched(true);
            }
        };

        fetchComposition();
    }, [composition, isStaticFallback]);

    if (isUniformPlayground) {
        return (
            <UniformPlayground
                resolveRenderer={resolveRenderer}
                contextualEditingEnhancer={({ composition }) =>
                    enhanceComposition(composition)
                }
            />
        );
    }

    return (
        isFetched && (
            <div>
                <UniformComposition
                    data={expectContent(data as RootComponentInstance)}
                    behaviorTracking="onLoad"
                    resolveRenderer={resolveRenderer}
                    contextualEditingEnhancer={({ composition }) =>
                        enhanceComposition(composition)
                    }
                />
            </div>
        )
    );
};
